import { Component, Input, OnInit } from '@angular/core';
import {
  RegistrationStepQuery,
  RegistrationStepService,
  StepType,
  LoggingService,
  ErrorStateService,
  WizardType,
  ModelMetaData,
  toNumber,
  RegistrationStepSettings,
} from '@fgb/core';
import { UntypedFormGroup } from '@angular/forms';
import { pluck } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ModelFormHelperService } from '@fgb/portal-component-library/src/lib/shared/forms/model-meta-data-form';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'fgbcl-registration-personal-details',
  templateUrl: './registration-personal-details.component.html',
  styleUrls: ['./registration-personal-details.component.scss'],
})
export class RegistrationPersonalDetailsComponent implements OnInit {
  readonly stepType = StepType.PersonalDetails;
  readonly wizardTypes = WizardType;
  wscKey = 'fgbcl.registration.personal.details';
  wizardType?: WizardType;
  loading: boolean = false;
  personalDetailsForm: UntypedFormGroup;
  stepData: any;
  models: ModelMetaData[];
  today = new Date();
  maxDate: NgbDateStruct;
  minDate: NgbDateStruct;
  @Input() minAge: number;
  @Input() maxAge: number;

  constructor(
    private registrationStepQuery: RegistrationStepQuery,
    private registrationStepService: RegistrationStepService,
    private logService: LoggingService,
    private translate: TranslateService,
    public errorService: ErrorStateService,
    private modelForm: ModelFormHelperService
  ) {}

  ngOnInit() {
    this.minAge = this.minAge || 18; // if input not provided, default to 18
    this.maxAge = this.maxAge || 100; // if input not provided, default to 100
    const step = this.registrationStepQuery.getStep(this.stepType);
    if (!step) {
      this.logService.warning('No step data found in the store for step', this.stepType);
      return;
    }
    this.wizardType = this.registrationStepQuery.getWizardType();
    const settings = this.registrationStepQuery.getStepSettings(this.stepType) as RegistrationStepSettings;

    if (settings?.MinAge) {
      this.minAge = toNumber(settings.MinAge);
    }
    if (settings?.MaxAge) {
      this.maxAge = toNumber(settings.MaxAge);
    }
    this.maxDate = { year: this.today.getFullYear() - this.minAge, month: this.today.getMonth() + 1, day: this.today.getDate() };
    this.minDate = {
      year: this.today.getFullYear() - this.maxAge,
      month: this.today.getMonth() + 1,
      day: this.today.getDate() + 1,
    };

    this.registrationStepService
      .fetchRegistrationStepForCurrentWizard(this.stepType, true)
      .pipe(pluck('Data'))
      .toPromise()
      .then((models) => {
        this.stepData = step.Data;
        this.models = models;
        this.personalDetailsForm = this.modelForm.buildForm(models, step.Data);
      });
  }

  submitStep() {
    this.errorService.clearErrors();
    this.personalDetailsForm.markAllAsTouched();

    if (!this.personalDetailsForm.valid) {
      this.logService.info('Form is invalid', this.personalDetailsForm);
      this.errorService.addError(`${this._getInvalidFields().toString()} are invalid.`);
      return;
    }

    this.loading = true;

    this._setShippingStepSkipStatus();
    this.logService.info('Attempting step submission.');

    const data = { ...this.stepData, ...this.modelForm.transformData(this.personalDetailsForm.value) };
    this.registrationStepService
      .submitRegistrationStepData(this.stepType, data)
      .toPromise()
      .then(() => (this.loading = false));
  }

  /**
   * Set the skip status of the shipping address step based on whether the checkbox was checked on this step or not.
   */
  private _setShippingStepSkipStatus() {
    if (!this.personalDetailsForm.get('ShouldUseShippingAddress')?.value) return;

    this.registrationStepService.setStepSkipStatus(
      StepType.ShippingAddress,
      !this.personalDetailsForm.get('ShouldUseShippingAddress')!.value
    );
  }

  private _getInvalidFields() {
    return Object.keys(this.personalDetailsForm.controls)
      .filter((key) => this.personalDetailsForm.get(key)?.invalid)
      .map((key) => this.translate.instant(`${this.wscKey}.${key.toLowerCase()}.label`));
  }
}
